import React, { useState, useEffect } from 'react';

interface EditableCellProps {
    value: any;
    onSave: (newValue: any) => Promise<void> | void;
    disabled?: boolean;
    fieldType?: 'text' | 'json' | 'multiline' | 'select';
    selectOptions?: string[];
}

const EditableCell: React.FC<EditableCellProps> = ({
    value,
    onSave,
    disabled = false,
    fieldType = 'text',
    selectOptions = []
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [saveStatus, setSaveStatus] = useState<'success' | 'error' | null>(null);
    const [validationError, setValidationError] = useState<string>('');

    // Initialize edit value when editing starts
    useEffect(() => {
        if (isEditing) {
            if (typeof value === 'object' && value !== null) {
                setEditValue(JSON.stringify(value, null, 2));
            } else {
                setEditValue(String(value || ''));
            }
        }
    }, [isEditing, value]);

    // Auto-clear status after delay
    useEffect(() => {
        if (saveStatus) {
            const timer = setTimeout(() => setSaveStatus(null), 2000);
            return () => clearTimeout(timer);
        }
    }, [saveStatus]);

    // Validate input based on field type
    const validateInput = (inputValue: string): boolean => {
        setValidationError('');

        if (fieldType === 'json') {
            try {
                JSON.parse(inputValue);
                return true;
            } catch (e) {
                setValidationError('Invalid JSON format');
                return false;
            }
        }

        if (fieldType === 'text' && inputValue.trim().length === 0) {
            setValidationError('Value cannot be empty');
            return false;
        }

        return true;
    };

    // Handle save operation with real API call
    const handleSave = async () => {
        if (!validateInput(editValue)) {
            return;
        }

        setIsSaving(true);
        setSaveStatus(null);

        try {
            let processedValue = editValue;

            // Process value based on field type
            if (fieldType === 'json') {
                processedValue = JSON.parse(editValue);
            } else if (fieldType === 'text' || fieldType === 'multiline') {
                processedValue = editValue.trim();
            } else if (fieldType === 'select') {
                processedValue = editValue;
            }

            // Call the real save function passed from parent
            await onSave(processedValue);

            // Success feedback
            setSaveStatus('success');
            setIsEditing(false);
            setValidationError('');

        } catch (error: any) {
            console.error('Cell save error:', error);
            setSaveStatus('error');
            setValidationError(error.message || 'Save failed');
        } finally {
            setIsSaving(false);
        }
    };

    // Handle cancel with unsaved changes check
    const handleCancel = () => {
        const originalValue = typeof value === 'object' && value !== null
            ? JSON.stringify(value, null, 2)
            : String(value || '');

        const hasChanges = editValue !== originalValue;

        if (hasChanges) {
            const confirmed = window.confirm('Discard unsaved changes?');
            if (!confirmed) return;
        }

        setIsEditing(false);
        setValidationError('');
        setSaveStatus(null);
    };

    // Handle keyboard shortcuts
    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey && fieldType !== 'multiline') {
            e.preventDefault();
            handleSave();
        } else if (e.key === 'Escape') {
            e.preventDefault();
            handleCancel();
        } else if (e.key === 'Enter' && e.ctrlKey && fieldType === 'multiline') {
            e.preventDefault();
            handleSave();
        }
    };

    // Display value formatting
    const formatDisplayValue = (val: any): string => {
        if (val === null || val === undefined) return 'null';
        if (typeof val === 'object') {
            const jsonStr = JSON.stringify(val, null, 2);
            return jsonStr.length > 100 ? jsonStr.substring(0, 100) + '...' : jsonStr;
        }
        const strVal = String(val);
        return strVal.length > 100 ? strVal.substring(0, 100) + '...' : strVal;
    };

    // Render different input types based on fieldType
    const renderInput = () => {
        const baseStyle = {
            width: '100%',
            padding: '0.25rem',
            border: validationError ? '2px solid #dc3545' : '1px solid #ccc',
            borderRadius: '3px',
            fontSize: '0.8rem'
        };

        if (fieldType === 'select' && selectOptions.length > 0) {
            return (
                <select
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    style={baseStyle}
                    disabled={isSaving}
                    autoFocus
                >
                    {selectOptions.map(option => (
                        <option key={option} value={option}>{option}</option>
                    ))}
                </select>
            );
        }

        if (fieldType === 'multiline' || fieldType === 'json') {
            return (
                <textarea
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    style={{
                        ...baseStyle,
                        minHeight: fieldType === 'json' ? '80px' : '60px',
                        fontFamily: fieldType === 'json' ? 'monospace' : 'inherit',
                        resize: 'vertical'
                    }}
                    disabled={isSaving}
                    autoFocus
                    placeholder={fieldType === 'json' ? 'Enter valid JSON...' : 'Enter text...'}
                />
            );
        }

        return (
            <input
                type="text"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onKeyDown={handleKeyDown}
                style={baseStyle}
                disabled={isSaving}
                autoFocus
            />
        );
    };

    if (isEditing) {
        return (
            <div style={{ position: 'relative' }}>
                {/* Input Field */}
                {renderInput()}

                {/* Validation Error */}
                {validationError && (
                    <div style={{
                        color: '#dc3545',
                        fontSize: '0.7rem',
                        marginTop: '0.25rem'
                    }}>
                        {validationError}
                    </div>
                )}

                {/* Action Buttons */}
                <div style={{
                    display: 'flex',
                    gap: '0.25rem',
                    marginTop: '0.25rem'
                }}>
                    <button
                        onClick={handleSave}
                        disabled={isSaving || !!validationError}
                        style={{
                            padding: '0.125rem 0.25rem',
                            fontSize: '0.7rem',
                            backgroundColor: isSaving ? '#ccc' : '#28a745',
                            color: 'white',
                            border: 'none',
                            borderRadius: '2px',
                            cursor: isSaving ? 'not-allowed' : 'pointer'
                        }}
                        title={fieldType === 'multiline' ? 'Ctrl+Enter to save' : 'Enter to save'}
                    >
                        {isSaving ? '💾' : '✓'}
                    </button>
                    <button
                        onClick={handleCancel}
                        disabled={isSaving}
                        style={{
                            padding: '0.125rem 0.25rem',
                            fontSize: '0.7rem',
                            backgroundColor: '#6c757d',
                            color: 'white',
                            border: 'none',
                            borderRadius: '2px',
                            cursor: isSaving ? 'not-allowed' : 'pointer'
                        }}
                        title="Esc to cancel"
                    >
                        ✕
                    </button>
                </div>

                {/* Save Status Indicator */}
                {saveStatus && (
                    <div style={{
                        position: 'absolute',
                        top: '-20px',
                        right: '0',
                        fontSize: '0.7rem',
                        color: saveStatus === 'success' ? '#28a745' : '#dc3545',
                        fontWeight: 'bold'
                    }}>
                        {saveStatus === 'success' ? ' Saved' : ' Error'}
                    </div>
                )}

                {/* Keyboard Shortcuts Hint */}
                <div style={{
                    fontSize: '0.65rem',
                    color: '#666',
                    marginTop: '0.25rem'
                }}>
                    {fieldType === 'multiline' ? 'Ctrl+Enter: Save' : 'Enter: Save'} • Esc: Cancel
                </div>
            </div>
        );
    }

    // Display mode
    return (
        <div
            onClick={() => !disabled && setIsEditing(true)}
            style={{
                cursor: disabled ? 'not-allowed' : 'pointer',
                padding: '0.25rem',
                borderRadius: '3px',
                border: '1px solid transparent',
                minHeight: '20px',
                opacity: disabled ? 0.6 : 1,
                transition: 'all 0.2s ease',
                ...(disabled ? {} : {
                    ':hover': {
                        backgroundColor: '#f8f9fa',
                        border: '1px solid #dee2e6'
                    }
                })
            }}
            title={disabled ? 'Editing disabled' : 'Click to edit (double-click for JSON fields)'}
            onDoubleClick={() => {
                if (!disabled && (fieldType === 'json' || fieldType === 'multiline')) {
                    setIsEditing(true);
                }
            }}
        >
            {/* Display Value */}
            <pre style={{
                margin: 0,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontSize: '0.8rem',
                fontFamily: fieldType === 'json' ? 'monospace' : 'inherit'
            }}>
                {formatDisplayValue(value)}
            </pre>

            {/* Edit Indicator */}
            {!disabled && (
                <div style={{
                    position: 'absolute',
                    top: '2px',
                    right: '2px',
                    fontSize: '0.6rem',
                    color: '#6c757d',
                    opacity: 0.7
                }}>
                    ✏️
                </div>
            )}

            {/* Status Overlay */}
            {saveStatus === 'success' && (
                <div style={{
                    position: 'absolute',
                    top: '0',
                    right: '0',
                    backgroundColor: '#28a745',
                    color: 'white',
                    fontSize: '0.6rem',
                    padding: '0.125rem 0.25rem',
                    borderRadius: '0 3px 0 3px'
                }}>
                    ✓ Saved
                </div>
            )}
        </div>
    );
};

export default EditableCell;